import React from 'react';
import Text from '@/components/Typography/Text';
import { Pressable, View } from 'react-native';
import { Link, useRouter } from 'expo-router';
import Icon from '@/components/Icon';
import { Section, useInteractive, useLanding } from '@/components/Landing/LandingKit';
import { fluid, isWeb } from '@/components/Landing/landingTokens';
import { pricingPalette } from './pricingPalette';

export function usePricing() {
  const layout = useLanding();
  const c = pricingPalette(Boolean(layout.isDark));
  return { ...layout, c, band: { bg: c.panel, ink: c.ink, inkSoft: c.inkSoft, muted: c.muted, faint: c.faint, rule: c.rule, surface: c.cardSolid } };
}

/** Keep genuine anchors for navigation and buttons for checkout actions. */
export function PricingButton({ label, href = '/signup', onPress, secondary = false, full = false }) {
  const { c } = usePricing();
  const router = useRouter();
  const { hovered, pressed, handlers } = useInteractive();
  const useLink = isWeb && href && !onPress;
  const button = (
    <Pressable
      {...handlers}
      accessibilityRole={useLink ? 'link' : 'button'}
      accessibilityLabel={label}
      onPress={useLink ? undefined : onPress || (() => router.push(href))}
      style={{ minHeight: 48, paddingVertical: 12, paddingHorizontal: 24, borderRadius: 12, alignSelf: full ? 'stretch' : 'flex-start', alignItems: 'center', justifyContent: 'center', backgroundColor: secondary ? (hovered ? c.panel : 'transparent') : c.ink, borderWidth: 1, borderColor: secondary ? c.ruleStrong : c.ink, opacity: pressed ? 0.7 : hovered && !secondary ? 0.85 : 1, ...(isWeb ? { cursor: 'pointer', transition: 'background-color 180ms ease, opacity 180ms ease' } : {}) }}
    >
      <Text weight="bold" style={{ color: secondary ? c.ink : c.page, fontSize: 15, lineHeight: 22 }}>{label}</Text>
    </Pressable>
  );
  return useLink ? <Link href={href} asChild>{button}</Link> : button;
}

export function PricingLink({ label, href }) {
  const { c } = usePricing();
  const router = useRouter();
  const link = <Pressable accessibilityRole="link" accessibilityLabel={label} onPress={isWeb ? undefined : () => router.push(href)} style={{ minHeight: 44, alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
    <Text weight="medium" style={{ color: c.ink, fontSize: 14, textDecorationLine: 'underline' }}>{label}</Text>
    <Icon name="arrow-right" size={11} color={c.ink} />
  </Pressable>;
  return isWeb ? <Link href={href} asChild>{link}</Link> : link;
}

export function PricingFeature({ feature }) {
  const { c } = usePricing();
  return <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
    <View aria-hidden style={{ width: 14, paddingTop: 5 }}><Icon name={feature.included ? 'check' : 'minus'} size={11} color={feature.included ? c.ink : c.muted} /></View>
    <Text style={{ flex: 1, minWidth: 0, color: feature.included ? c.inkSoft : c.muted, fontSize: 14, lineHeight: 22 }}>{feature.name}</Text>
  </View>;
}

/** The same quiet headings and section rhythm as the landing and about pages. */
export function PricingSection({ label, background, children }) {
  const { c, width } = usePricing();
  return (
    <Section label={label} max={1200} background={background} style={{ borderTopWidth: 1, borderTopColor: c.rule }} contentStyle={{ paddingTop: fluid(width, 56, 88), paddingBottom: fluid(width, 56, 88) }}>
      {children}
    </Section>
  );
}

export function PricingTitle({ children, align }) {
  const { c, width } = usePricing();
  const size = fluid(width, 30, 42);
  return (
    <Text tag="h2" weight="bold" style={{ color: c.ink, fontSize: size, lineHeight: Math.round(size * 1.15), letterSpacing: -size * 0.035, textAlign: align }}>
      {children}
    </Text>
  );
}
